import React, { useEffect, useRef } from 'react';
import { useBackgroundTask } from '../contexts/BackgroundTaskContext';
import config from '../config';

/**
 * BackgroundJobPoller
 *
 * This component runs globally (mounted in App.js) and polls the status of ALL
 * background jobs that are in 'processing' state. It continues polling even when
 * the user navigates away from the Host Comparison page.
 *
 * This solves the issue where jobs would get stuck at X% when navigating to other pages.
 */
const BackgroundJobPoller = () => {
  const { tasks, updateTask } = useBackgroundTask();
  const pollIntervalsRef = useRef({});

  const API_URL = config.API_BASE_URL;

  /**
   * Determine the correct status endpoint based on task type
   */
  const getStatusEndpoint = (task) => {
    const jobId = task.jobId || task.id;

    // Host Comparison
    if (task.title.includes('Host Comparison')) {
      return `${API_URL}/mrca/compare-hosts-status/${jobId}`;
    }

    // MRCA Analysis types
    if (task.title.includes('Hierarchical MRCA')) {
      return `${API_URL}/mrca/hierarchical-status/${jobId}`;
    }
    if (task.title.includes('Comparative MRCA')) {
      return `${API_URL}/mrca/comparative-status/${jobId}`;
    }
    if (task.title.includes('Single Level MRCA')) {
      return `${API_URL}/mrca/find-by-level-status/${jobId}`;
    }
    if (task.title.includes('Isolate') && task.title.includes('MRCA')) {
      return `${API_URL}/mrca/find-by-isolates-status/${jobId}`;
    }

    // Default fallback
    return `${API_URL}/mrca/compare-hosts-status/${jobId}`;
  };

  useEffect(() => {
    // Find all processing tasks
    const processingTasks = tasks.filter(task => task.status === 'processing');

    // For each processing task, ensure we have a polling interval
    processingTasks.forEach(task => {
      // Skip if we're already polling this job
      if (pollIntervalsRef.current[task.id]) {
        return;
      }

      // Skip if no jobId yet (it might be waiting for API response)
      if (!task.jobId) {
        console.log(`[BackgroundJobPoller] Skipping task ${task.id} - no jobId yet`);
        return;
      }

      console.log(`[BackgroundJobPoller] Starting to poll job: ${task.id} (jobId: ${task.jobId})`);

      // Start polling for this job
      const pollJob = async () => {
        try {
          // Get the correct endpoint for this task type
          const statusEndpoint = getStatusEndpoint(task);

          const statusResponse = await fetch(statusEndpoint);

          if (!statusResponse.ok) {
            console.error(`[BackgroundJobPoller] Status endpoint returned ${statusResponse.status}: ${statusEndpoint}`);
            throw new Error('Failed to check job status');
          }

          const statusData = await statusResponse.json();

          console.log(`[BackgroundJobPoller] Job ${task.id} (${task.title}) status:`, statusData.status, 'progress:', statusData.progress);

          // Update task with progress (only if still processing)
          updateTask(task.id, {
            progress: statusData.progress || 10,
            subtitle: statusData.subtitle || task.subtitle
          });

          // Check if job is complete
          if (statusData.status === 'completed') {
            console.log(`[BackgroundJobPoller] ✅ Job ${task.id} marked as COMPLETED on backend`);
            console.log(`[BackgroundJobPoller] Backend result:`, statusData.result);
            console.log(`[BackgroundJobPoller] Task title:`, task.title);

            const updateData = {
              status: 'completed',
              progress: 100,
              resultMessage: statusData.result?.totalMutations
                ? `Found ${statusData.result.totalMutations} significant mutations`
                : 'Analysis complete',
              results: statusData.result
            };

            console.log(`[BackgroundJobPoller] Updating task with:`, updateData);
            updateTask(task.id, updateData);

            // Auto-dispatch event to show results on the analysis page
            setTimeout(() => {
              console.log(`[BackgroundJobPoller] Auto-dispatching viewMRCAResults event for completed job`);
              window.dispatchEvent(
                new CustomEvent('viewMRCAResults', {
                  detail: { task: { ...task, ...updateData } }
                })
              );
            }, 100);

            // Clear the polling interval for this job
            console.log(`[BackgroundJobPoller] Stopping polling for job ${task.id}`);
            clearInterval(pollIntervalsRef.current[task.id]);
            delete pollIntervalsRef.current[task.id];
          } else if (statusData.status === 'failed') {
            // Only update if the failure reason doesn't indicate user cancellation
            // (avoid overwriting the user's cancellation message)
            const isCancelled = statusData.error && statusData.error.includes('cancelled');

            console.log(`[BackgroundJobPoller] Job ${task.id} failed. Cancelled: ${isCancelled}`);

            updateTask(task.id, {
              status: 'failed',
              resultMessage: statusData.error || 'Job processing failed'
            });

            // Clear the polling interval for this job
            clearInterval(pollIntervalsRef.current[task.id]);
            delete pollIntervalsRef.current[task.id];
          }
        } catch (err) {
          console.error(`[BackgroundJobPoller] Polling error for job ${task.id}:`, err);
          // Continue polling - don't fail on single error
        }
      };

      // Poll every 500ms
      const intervalId = setInterval(pollJob, 500);
      pollIntervalsRef.current[task.id] = intervalId;

      // Do initial poll immediately
      pollJob();
    });

    // Cleanup: clear intervals for completed/failed tasks
    const completedOrFailedTasks = new Set();
    Object.keys(pollIntervalsRef.current).forEach(taskId => {
      const task = tasks.find(t => t.id === taskId);
      if (!task || task.status !== 'processing') {
        console.log(`[BackgroundJobPoller] Stopping polling for task ${taskId} (status: ${task?.status || 'not found'})`);
        clearInterval(pollIntervalsRef.current[taskId]);
        delete pollIntervalsRef.current[taskId];
        completedOrFailedTasks.add(taskId);
      }
    });
  }, [tasks, updateTask, API_URL]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(pollIntervalsRef.current).forEach(intervalId => {
        clearInterval(intervalId);
      });
    };
  }, []);

  // This component doesn't render anything
  return null;
};

export default BackgroundJobPoller;
